/* global SDG */
import { isComingSoon } from "./store-badges";
import getCachedGeolocation from "./get-cached-geolocation";

/**
 * Fetch Stores Data
 * Standalone module to fetch and populate store location data in sessionStorage.
 * Can be imported without initializing the store landing page.
 */

/**
 * Fetches Stockist store location data and populates sessionStorage.
 *
 * Returns two views of the same proximity-sorted list:
 *   - `stores`: every location from Stockist, including "Coming Soon" stores.
 *     Used by the Find a Store landing page, which shows pre-opening stores
 *     so customers can see them.
 *   - `nearestStoresData`: same list with "Coming Soon" stores removed. Also
 *     cached in sessionStorage under the same key for the footer, BOPIS,
 *     Frame Review, and Cart Drop Off — surfaces that should only show
 *     stores customers can actually shop at today.
 *
 * @returns {Promise<{stores: Array, nearestStoresData: Array, nearestStore: object, ipStackState: string}|undefined>}
 */
export default function fetchStoresData() {
  const { ipStackApiKey } = SDG.Data;
  const widgetTag = SDG.Data.stockistWidgetTag || null;
  const storage = window.sessionStorage;

  if (!ipStackApiKey || !widgetTag) {
    return Promise.resolve();
  }

  function doFetch(geolocationPosition = null) {
    let coords = null;

    if (geolocationPosition !== null) {
      coords = {
        latitude: geolocationPosition.coords.latitude,
        longitude: geolocationPosition.coords.longitude
      };
    }

    return fetch(`http://api.ipstack.com/check?access_key=${ipStackApiKey}`)
      .then((resp) => resp.json())
      .then(async (ipStackResp) => {
        if (coords === null) {
          const { latitude, longitude } = ipStackResp;
          coords = { latitude, longitude };
        }

        const ipStackDataJson = storage?.getItem("ipStackData");
        const ipStackData = ipStackDataJson ? JSON.parse(ipStackDataJson) : null;
        const { state: ipStackState } = ipStackData || {};
        const nearestStoreJson = storage?.getItem("nearestStoresData");
        const nearestStores = nearestStoreJson ? JSON.parse(nearestStoreJson) : null;

        if (ipStackState !== ipStackResp.region_code) {
          storage?.setItem("ipStackData", JSON.stringify(ipStackResp));
        }

        const { latitude, longitude } = coords;

        const searchResult = await fetch(
          `https://stockist.co/api/v1/${widgetTag}/locations/search?latitude=${latitude}&longitude=${longitude}`
        ).then((resp) => resp.json());

        const stores = searchResult.locations;
        let nearestStoresData;

        if (!nearestStores || !nearestStoreJson) {
          const filteredStores = stores.filter((store) => !isComingSoon(store));
          storage?.setItem("nearestStoresData", JSON.stringify(filteredStores));
          nearestStoresData = filteredStores;
        } else {
          nearestStoresData = JSON.parse(storage?.getItem("nearestStoresData") || "[]");
        }

        const nearestStore = nearestStoresData?.length ? nearestStoresData[0] : null;

        return {
          stores,
          nearestStoresData,
          nearestStore,
          ipStackState: ipStackResp.region_code || ipStackState
        };
      })
      .catch((err) => {
        console.error(err);
        return undefined;
      });
  }

  return getCachedGeolocation().then((coords) => doFetch(coords ? { coords } : null));
}
